import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Eye, EyeOff, FileSpreadsheet, History, Loader2, Lock,
  ShieldCheck, User,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";

const FEATURES = [
  { icon: ShieldCheck, title: "Hak Akses Terstruktur", desc: "Kelola pengguna dan hak akses sesuai peran." },
  { icon: FileSpreadsheet, title: "Integrasi Excel", desc: "Import data transaksi dengan cepat dan mudah." },
  { icon: History, title: "Audit Trail Lengkap", desc: "Lacak setiap perubahan dengan riwayat aktivitas." },
];

const BARS = [42, 68, 50, 88, 60, 104, 78, 122, 96, 140, 112, 160];

function GlowChart() {
  const pts = "20,210 90,185 160,196 230,150 300,165 370,120 440,132 510,88 580,100 650,60";
  return (
    <svg
      viewBox="0 0 680 260"
      className="absolute bottom-0 left-0 w-full opacity-70 pointer-events-none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="barGrad" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#1D4ED8" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#38BDF8" stopOpacity="0.55" />
        </linearGradient>
        <filter id="glowFx" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="5" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      {BARS.map((h, i) => (
        <rect
          key={i}
          x={28 + i * 55}
          y={250 - h}
          width="26"
          height={h}
          rx="5"
          fill="url(#barGrad)"
        />
      ))}
      <polyline
        points={pts}
        fill="none"
        stroke="#38BDF8"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        filter="url(#glowFx)"
      />
      {pts.split(" ").map((p, i) => {
        const [cx, cy] = p.split(",");
        return (
          <circle key={i} cx={cx} cy={cy} r="4" fill="#7DD3FC" filter="url(#glowFx)" className="animate-pulse" />
        );
      })}
    </svg>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const saved = localStorage.getItem("imf831_identifier");
    if (saved) {
      setIdentifier(saved);
      setRemember(true);
    }
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(identifier, password);
      if (remember) localStorage.setItem("imf831_identifier", identifier);
      else localStorage.removeItem("imf831_identifier");
      navigate("/", { replace: true });
    } catch (err) {
      setError(errMsg(err, "Login gagal. Periksa email/username dan kata sandi."));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 rounded-xl pl-10 pr-10 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none transition-all";

  const dateStr = now.toLocaleDateString("id-ID", { weekday: "long", day: "2-digit", month: "short", year: "numeric" });
  const timeStr = now.toLocaleTimeString("id-ID", { hour12: false });

  return (
    <div className="min-h-screen flex flex-col lg:grid lg:grid-cols-[1.15fr_1fr] bg-[#070E1B]">
      <div className="hidden lg:flex flex-col relative overflow-hidden bg-gradient-to-br from-[#070E1B] via-[#0A1428] to-[#0F172A] p-12 pb-0">
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-blue-600/15 blur-3xl pointer-events-none" />
        <div className="absolute left-1/4 top-1/3 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
        <GlowChart />

        <div className="relative flex items-center gap-3 fade-up" style={{ animationDelay: "0.1s" }}>
          <img
            src="/assets/logo-imf.png"
            alt="Logo IMF-831"
            className="w-11 h-11 rounded-xl shadow-lg shadow-blue-500/40"
          />
          <div>
            <p className="text-white font-bold text-lg tracking-tight leading-tight font-heading">IMF-831</p>
            <p className="text-[10px] tracking-[0.25em] text-slate-400 uppercase">Sistem Laporan Kas & Keuangan</p>
          </div>
        </div>

        <div className="relative mt-auto pt-16 pb-10">
          <h1
            className="font-heading text-5xl xl:text-6xl font-bold leading-[1.1] text-white max-w-xl fade-up"
            style={{ animationDelay: "0.3s" }}
          >
            Kendali Keuangan.
            <br />
            <span className="bg-gradient-to-r from-blue-500 to-cyan-400 bg-clip-text text-transparent">
              Transparan.
            </span>
            <br />
            Akurat. Terpercaya.
          </h1>
          <p className="text-slate-400 text-sm mt-6 max-w-md leading-relaxed fade-up" style={{ animationDelay: "0.45s" }}>
            Sistem terintegrasi untuk pencatatan pemasukan dan pengeluaran, perhitungan saldo
            otomatis, import Excel, multi-user dengan hak akses, dan audit trail lengkap.
          </p>

          <div className="grid grid-cols-3 gap-4 mt-10 max-w-xl fade-up" style={{ animationDelay: "0.6s" }}>
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md p-4 transition-all hover:border-cyan-400/40 hover:bg-white/[0.07]"
              >
                <f.icon className="w-6 h-6 text-cyan-400" />
                <p className="text-sm font-semibold text-slate-100 mt-3">{f.title}</p>
                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">{f.desc}</p>
                <div className="w-8 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-400 mt-3 rounded-full" />
              </div>
            ))}
          </div>

          <div
            className="inline-flex items-center gap-3 mt-10 rounded-full bg-white/5 border border-white/10 backdrop-blur-md px-4 py-2 fade-up"
            style={{ animationDelay: "0.75s" }}
            data-testid="login-status-pill"
          >
            <span className="flex items-center gap-2 text-[11px] font-semibold text-slate-200">
              <span className="led-dot" /> Sistem Online
            </span>
            <span className="w-px h-3.5 bg-white/15" />
            <span className="font-num text-[11px] text-slate-400">
              {dateStr} · {timeStr} WIB
            </span>
          </div>

          <p className="text-[11px] text-slate-500 mt-8 fade-up" style={{ animationDelay: "0.85s" }}>
            © 2026 <span className="text-blue-400 font-semibold">IMF-831</span> — Sistem Laporan Kas & Keuangan
            <span className="mx-2 text-slate-600">|</span> Versi 1.0.0
          </p>
        </div>
      </div>

      <div className="relative flex-1 flex items-center justify-center p-6 bg-gradient-to-br from-[#F0F4F8] to-[#E2E8F0] overflow-hidden">
        <svg
          viewBox="0 0 600 600"
          className="absolute inset-0 w-full h-full opacity-[0.35] pointer-events-none"
          aria-hidden="true"
          preserveAspectRatio="xMidYMid slice"
        >
          <path d="M-50 480 C 150 400, 300 560, 650 440" fill="none" stroke="#CBD5E1" strokeWidth="1.5" />
          <path d="M-50 520 C 180 430, 320 600, 650 480" fill="none" stroke="#CBD5E1" strokeWidth="1" />
          <path d="M-50 120 C 150 60, 350 180, 650 80" fill="none" stroke="#CBD5E1" strokeWidth="1" />
        </svg>

        <div className="lg:hidden absolute top-6 left-6 flex items-center gap-3">
          <img src="/assets/logo-imf.png" alt="Logo IMF-831" className="w-10 h-10 rounded-xl shadow-md" />
          <div>
            <p className="text-slate-900 font-bold tracking-tight leading-tight font-heading">IMF-831</p>
            <p className="text-[10px] tracking-[0.2em] text-slate-500 uppercase">Sistem Laporan Kas & Keuangan</p>
          </div>
        </div>

        <div
          className="relative w-full max-w-md bg-white/95 border border-slate-200/80 rounded-3xl shadow-2xl shadow-slate-400/30 p-8 fade-up"
          style={{ animationDelay: "0.25s" }}
          data-testid="login-card"
        >
          <div className="flex justify-center mb-5">
            <div className="relative w-24 h-24 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full border-2 border-dashed border-blue-300 animate-spin-slow" />
              <img
                src="/assets/logo-imf.png"
                alt="Logo IMF-831"
                className="w-16 h-16 rounded-full shadow-lg shadow-blue-500/30"
              />
            </div>
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900 text-center">
            Selamat Datang
          </h2>
          <p className="text-xs text-slate-500 text-center mt-1.5">
            Masuk untuk mengakses sistem Laporan Kas & Keuangan.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-700">Email / Username</label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="input-login-email"
                  type="text"
                  required
                  className={inputCls}
                  placeholder="email@organisasi.id atau username"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-700">Kata Sandi</label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="input-login-password"
                  type={showPassword ? "text" : "password"}
                  required
                  className={inputCls}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  data-testid="btn-toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="checkbox-remember-me"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-slate-300 focus:ring-blue-500"
                  style={{ accentColor: "#2563EB", colorScheme: "light" }}
                />
                Ingat saya
              </label>
              <button
                type="button"
                data-testid="link-forgot-password"
                onClick={() => setShowHint((v) => !v)}
                className="text-xs font-semibold text-blue-600 hover:text-blue-700"
              >
                Lupa kata sandi?
              </button>
            </div>
            {showHint && (
              <p className="text-[11px] text-slate-500 -mt-2" data-testid="forgot-password-hint">
                Hubungi Super Admin untuk reset password akun Anda.
              </p>
            )}

            {error && (
              <div data-testid="login-error" className="border border-rose-200 bg-rose-50 rounded-xl px-3 py-2 text-sm text-rose-700">
                {error}
              </div>
            )}
            <button
              data-testid="btn-login-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm shadow-lg shadow-blue-500/25 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Masuk
              <ArrowRight className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-2.5 pt-4 border-t border-slate-100">
              <Lock className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-slate-500 leading-relaxed">
                <span className="font-semibold text-slate-700">Aman • Terpercaya • Terintegrasi</span>
                <br />
                Data Anda dilindungi dan aktivitas tercatat secara otomatis.
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
